import { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const t = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

// Admin pre-flight before pressing Deploy in the Emergent panel: Production live count + explicit confirmation.
export default function DeploySafetyCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const run = () => api.get("/admin/deploy-safety").then(({ data }) => { if (alive) { setData(data); setErr(null); } }).catch((e) => alive && setErr(e?.response?.data?.detail || "Check failed"));
    run();
    const i = setInterval(run, 10000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  const ack = async () => {
    if (!checked) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/deploy-safety/ack", { confirm: true });
      setData((d) => ({ ...d, ack: r.ack }));
      setChecked(false);
      toast.success(`Acknowledged — ${r.ack.live_count} live broadcast(s) will be interrupted. Cleared for ${data?.ack_window_minutes} min.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record confirmation"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="card-surface p-4 text-sm text-rose-300" data-testid="deploy-safety-error">Deploy safety check unavailable: {err}</div>;
  if (!data) return null;
  const ackActive = data.ack && new Date(data.ack.expires_at) > new Date();

  return (
    <div className={`border p-4 space-y-3 ${data.safe ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/50 bg-rose-500/10"}`} data-testid="deploy-safety-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-white">
          {data.safe ? <ShieldCheck size={16} className="text-emerald-400" /> : <ShieldAlert size={16} className="text-rose-400" />}
          <span className="uppercase tracking-widest text-xs text-zinc-400">Deploy safety</span>
          <span data-testid="deploy-safety-status" className={data.safe ? "text-emerald-300" : "text-rose-300 font-semibold"}>
            {data.safe ? "No streams live — safe to deploy" : data.warning}
          </span>
        </div>
        <div className="text-[11px] text-zinc-500 flex items-center gap-1"><RefreshCw size={10} /> {data.source === "local" ? "this environment" : "Production"} · 10 s</div>
      </div>

      {!data.safe && (
        <>
          <ul className="text-xs text-zinc-300 space-y-1" data-testid="deploy-safety-streams">
            {data.streams.map((s) => (
              <li key={s.id} className="flex gap-3"><span className="text-rose-400">●</span><b className="text-white">@{s.host || "?"}</b><span className="text-zinc-400 truncate">{s.title || "untitled"}</span><span className="text-zinc-500">{s.viewer_count} viewer(s) · since {t(s.started_at)}</span></li>
            ))}
          </ul>
          {ackActive ? (
            <div className="text-xs text-amber-300" data-testid="deploy-safety-acked">Confirmed by @{data.ack.admin_handle} at {t(data.ack.created_at)} — cleared to deploy until {t(data.ack.expires_at)}. Interrupting {data.ack.live_count} broadcast(s).</div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-zinc-200 cursor-pointer">
                <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="deploy-safety-confirm-checkbox" />
                I understand this will interrupt {data.count} active broadcast(s)
              </label>
              <Button size="sm" variant="destructive" disabled={!checked || busy} onClick={ack} data-testid="deploy-safety-confirm-btn">Yes, I&apos;m sure — record confirmation</Button>
              <span className="text-[11px] text-zinc-500">Or wait: this card turns green the moment the last stream ends.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
